// src/utils/branding/contrast.ts
/**
 * @file WCAG contrast maths for the clinic brand colour (S9, ClickUp 86d3w1g8u).
 * @module utils/branding/contrast
 *
 * *** THIS WARNS. IT NEVER BLOCKS. ***
 *
 * Decision D12: a brand colour that fails 4.5:1 is reported — naming the ratio and the
 * theme it fails in — and the clinic saves it anyway. The clinic owns its brand and may
 * legitimately want a colour that only works on one surface. Nothing in this module
 * returns something a caller is meant to gate a save on; `failingContrasts()` drives an
 * `Alert`, not a `disabled`.
 *
 * ⚠️ Deliberately standalone rather than reusing `src/utils/colorUtils.ts`. That file has
 * the same luminance formula, but `parseRGB` and `relativeLuminance` are module-private
 * there and its only export (`getReadableTextColor`) answers a different question — which
 * of black/white to put ON a background, not what ratio two given colours make. Exporting
 * its internals would mean editing a file used by every tag in the app for a settings
 * screen; if a third consumer ever appears, merge the two then.
 */

/** WCAG 2.1 AA for normal-size text. */
export const WCAG_AA_NORMAL = 4.5;

/** The light theme's card surface — `LIGHT_SURFACE['--mg-bg-card']` in `themeUtils.ts`. */
export const LIGHT_SURFACE_HEX = '#ffffff';

/** The dark theme's card surface — `DARK_SURFACE['--mg-bg-card']` in `themeUtils.ts`. */
export const DARK_SURFACE_HEX = '#161616';

export interface ContrastResult {
	/** Which theme's surface this result is about — the half the warning copy names. */
	theme: 'light' | 'dark';
	/** The surface the brand colour was measured against. */
	surface: string;
	/** The contrast ratio, rounded to 2dp — the number shown to the user. */
	ratio: number;
	/** `ratio >= 4.5`, measured on the ROUNDED value. See `contrastRatio`. */
	passes: boolean;
}

/**
 * Parse `#rgb` or `#rrggbb` into an sRGB triplet. Anything else — a named colour, an
 * `rgb()` string, `#0F766E;color:red` — is `null`, and a `null` here means "no opinion",
 * never "fails".
 *
 * S4 accepts 6-digit hex only, so 3-digit is tolerated purely because the picker can hand
 * one back mid-edit before the value is normalised for submission.
 */
export const parseHexColor = (value: string | null | undefined): [number, number, number] | null => {
	if (typeof value !== 'string') return null;

	const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value.trim());
	if (short) {
		return [
			parseInt(short[1] + short[1], 16),
			parseInt(short[2] + short[2], 16),
			parseInt(short[3] + short[3], 16),
		];
	}

	const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(value.trim());
	if (full) {
		return [parseInt(full[1], 16), parseInt(full[2], 16), parseInt(full[3], 16)];
	}

	return null;
};

/**
 * WCAG 2.1 relative luminance of an sRGB triplet.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
	const toLinear = (channel: number): number => {
		const s = channel / 255;
		return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
	};

	return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * The WCAG contrast ratio between two colours, rounded to 2dp, or `null` when either
 * colour is unparseable.
 *
 * ⚠️ Rounded BEFORE the comparison in {@link checkBrandColorContrast}, on purpose. A raw
 * ratio of 4.4996 displays as "4.50:1"; reporting that as a failure next to the threshold
 * it appears to meet reads as a bug in the warning rather than a property of the colour.
 */
export const contrastRatio = (a: string | null | undefined, b: string | null | undefined): number | null => {
	const rgbA = parseHexColor(a);
	const rgbB = parseHexColor(b);
	if (!rgbA || !rgbB) return null;

	const lumA = relativeLuminance(rgbA);
	const lumB = relativeLuminance(rgbB);
	const lighter = Math.max(lumA, lumB);
	const darker = Math.min(lumA, lumB);

	return Math.round(((lighter + 0.05) / (darker + 0.05)) * 100) / 100;
};

/**
 * Measure a candidate brand colour against BOTH theme card surfaces.
 *
 * Returns `[]` for an absent or unparseable colour — "we cannot say" must never render as
 * a warning, or clearing the colour would leave an accusation on screen.
 */
export const checkBrandColorContrast = (hex: string | null | undefined): ContrastResult[] => {
	const surfaces: { theme: 'light' | 'dark'; surface: string }[] = [
		{ theme: 'light', surface: LIGHT_SURFACE_HEX },
		{ theme: 'dark', surface: DARK_SURFACE_HEX },
	];

	return surfaces.flatMap(({ theme, surface }) => {
		const ratio = contrastRatio(hex, surface);
		if (ratio === null) return [];
		return [{ theme, surface, ratio, passes: ratio >= WCAG_AA_NORMAL }];
	});
};

/**
 * Only the surfaces the colour fails against — what decides whether the warning appears.
 *
 * 🚨 **NO SINGLE COLOUR CAN PASS BOTH SURFACES, AND THAT IS ARITHMETIC, NOT A BUG.**
 * Passing 4.5:1 against `#ffffff` requires a relative luminance of at most 0.1833; passing
 * it against `#161616` requires at least 0.2111. The two windows do not overlap, so this
 * function returns a non-empty list for EVERY parseable colour. Verified: `#0f766e` is
 * 5.47:1 light / 3.31:1 dark, `#ffff00` is 1.07:1 / 16.85:1, mid-grey `#808080` is
 * 3.95:1 / 4.58:1 — the closest either side gets.
 *
 * That is exactly why decision D12 makes this a warning and never a blocker: a gate built
 * on it would refuse every colour a clinic could ever choose. The value of the check is
 * telling an administrator WHICH theme their colour is weak on, not whether to allow it —
 * so the screen renders every result, passing ones included, rather than only these.
 */
export const failingContrasts = (hex: string | null | undefined): ContrastResult[] =>
	checkBrandColorContrast(hex).filter((result) => !result.passes);

/** `4.5` → `"4.50:1"`. Always 2dp, so two ratios in one sentence line up. */
export const formatRatio = (ratio: number): string => `${ratio.toFixed(2)}:1`;
